import { ImageIcon, Plus } from 'lucide-react';
import { Link } from 'react-router';

import { PermissionGate } from '@/auth/PermissionGate';
import { useCan } from '@/auth/queries';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Paginador } from '@/components/Paginador';
import { useFiltrosUrl } from '@/hooks/useFiltrosUrl';

import { nombreEspecialidad, useBanco, useEspecialidades } from './queries';

const CLAVES = ['q', 'specialtyId', 'online', 'quiz', 'english', 'source', 'withReports'] as const;

function Banco() {
  const { filtros, cambiar, query } = useFiltrosUrl(CLAVES);
  const editable = useCan('preguntas', 'editar');
  const esp = useEspecialidades();
  const q = useBanco({ ...query, page: filtros.page, pageSize: 25 });
  const flag = (k: 'online' | 'quiz' | 'english' | 'withReports', etiqueta: string) => (
    <select
      className="input-vidrio"
      value={filtros[k]}
      onChange={(e) => cambiar({ [k]: e.target.value })}
      aria-label={etiqueta}
    >
      <option value="">{etiqueta}: todos</option>
      <option value="true">{etiqueta}: sí</option>
      <option value="false">{etiqueta}: no</option>
    </select>
  );
  return (
    <>
      <Encabezado
        titulo="Banco de casos"
        descripcion="Casos clínicos del simulador, exámenes, online y quiz. La respuesta correcta siempre es la opción A."
        acciones={
          editable && (
            <Link to="/preguntas/nueva" className="btn-cs btn-primario">
              <Plus size={16} aria-hidden /> Nuevo caso
            </Link>
          )
        }
      />
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
        <input
          className="input-vidrio xl:col-span-2"
          placeholder="ID, código o texto"
          defaultValue={filtros.q}
          onKeyDown={(e) => e.key === 'Enter' && cambiar({ q: e.currentTarget.value.trim() })}
          onBlur={(e) => cambiar({ q: e.target.value.trim() })}
          aria-label="Buscar"
        />
        <select
          className="input-vidrio"
          value={filtros.specialtyId}
          onChange={(e) => cambiar({ specialtyId: e.target.value })}
          aria-label="Especialidad"
        >
          <option value="">Todas las especialidades</option>
          {esp.data?.map((e) => (
            <option key={e.id} value={e.id}>
              {nombreEspecialidad(e)} ({e.questions})
            </option>
          ))}
        </select>
        {flag('online', 'Online')}
        {flag('quiz', 'Quiz')}
        {flag('english', 'Inglés')}
        {flag('withReports', 'Con reportes')}
      </div>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data?.data.length === 0 && <Vacio titulo="Sin casos con esos filtros" />}
      {q.data && q.data.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>ID</th>
                <th>Caso / pregunta</th>
                <th>Especialidad</th>
                <th>Distribución</th>
                <th>Origen</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {q.data.data.map((p) => (
                <tr key={p.id}>
                  <td className="tabular">{p.id}</td>
                  <td className="max-w-xl">
                    <Link to={`/preguntas/${p.id}`} className="font-semibold">
                      {(p.clinicalCase ?? p.prompt).slice(0, 140)}
                      {(p.clinicalCase ?? p.prompt).length > 140 && '…'}
                    </Link>
                    <p className="text-xs text-texto-3">
                      {p.prompt.slice(0, 100)}
                      {p.children > 0 && ` · +${p.children} subsecuente(s)`}
                    </p>
                  </td>
                  <td>{p.specialtyName ?? '—'}</td>
                  <td className="space-x-1 whitespace-nowrap">
                    {p.isOnline && <span className="insignia">Online</span>}
                    {p.isQuiz && <span className="insignia">Quiz</span>}
                    {p.isEnglish && <span className="insignia">EN</span>}
                  </td>
                  <td className="text-xs">{p.source ?? '—'}</td>
                  <td className="whitespace-nowrap">
                    {p.hasImage && (
                      <ImageIcon
                        size={16}
                        aria-label="Con imagen"
                        className="inline text-texto-3"
                      />
                    )}
                    {p.openReports > 0 && (
                      <span className="insignia insignia-exclusivo ml-1">
                        {p.openReports} reporte(s)
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
    </>
  );
}

export function BancoPage() {
  return (
    <PermissionGate seccion="preguntas">
      <Banco />
    </PermissionGate>
  );
}
